"use client";

import { useCallback } from "react";
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
} from "@tillix/ui-atoms/combobox";
import {
  FieldControl,
  FieldError,
  FieldLabel,
  FieldRoot,
} from "@tillix/ui-atoms/field";
import { Fieldset } from "@tillix/ui-atoms/fieldset";
import { Input } from "@tillix/ui-atoms/input";
import { Controller, useFormContext } from "react-hook-form";
import { useGooglePlaces } from "../hooks/useGooglePlaces";
import type { NigoFormValues } from "../types";

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
] as const;

const zipCodeRegex = /^\d{5}$/;

function getComponent(
  components: google.maps.GeocoderAddressComponent[],
  type: string,
): string {
  return components.find((c) => c.types.includes(type))?.short_name ?? "";
}

export default function AddressSection() {
  const { control, setValue } = useFormContext<NigoFormValues>();

  const onPlaceSelect = useCallback(
    (place: google.maps.places.PlaceResult) => {
      const components = place.address_components;
      if (!components) return;

      const streetNumber = getComponent(components, "street_number");
      const route = getComponent(components, "route");
      const city =
        getComponent(components, "locality") ||
        getComponent(components, "sublocality_level_1");
      const state = getComponent(components, "administrative_area_level_1");
      const zip = getComponent(components, "postal_code");

      setValue("street1", `${streetNumber} ${route}`.trim(), {
        shouldValidate: true,
      });
      setValue("city", city, { shouldValidate: true });
      setValue("state", state, { shouldValidate: true });
      setValue("zip", zip, { shouldValidate: true });
    },
    [setValue],
  );

  const { inputRef } = useGooglePlaces({ onPlaceSelect });

  return (
    <Fieldset className="max-w-full">
      <Fieldset.Legend>Address</Fieldset.Legend>

      <Controller
        control={control}
        name="street1"
        render={({ field, fieldState }) => (
          <FieldRoot invalid={!!fieldState.error} name={field.name}>
            <FieldLabel>Street Address</FieldLabel>
            <FieldControl
              render={(defaultProps) => (
                <Input
                  {...defaultProps}
                  autoComplete="off"
                  className={defaultProps.className ?? ""}
                  maxLength={100}
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  placeholder="Start typing an address..."
                  ref={(el) => {
                    field.ref(el);
                    inputRef.current = el;
                  }}
                  style={defaultProps.style ?? {}}
                  value={field.value}
                />
              )}
            />
            {fieldState.error && (
              <FieldError match={true}>{fieldState.error.message}</FieldError>
            )}
          </FieldRoot>
        )}
        rules={{
          required: "Street address is required",
          minLength: { value: 3, message: "Street address is too short" },
          maxLength: { value: 100, message: "Street address is too long" },
        }}
      />

      <Controller
        control={control}
        name="street2"
        render={({ field, fieldState }) => (
          <FieldRoot invalid={!!fieldState.error} name={field.name}>
            <FieldLabel>Street Address 2 (Optional)</FieldLabel>
            <FieldControl
              render={(defaultProps) => (
                <Input
                  {...defaultProps}
                  {...field}
                  autoComplete="off"
                  className={defaultProps.className ?? ""}
                  placeholder="Apt, Suite, Unit, etc."
                  style={defaultProps.style ?? {}}
                />
              )}
            />
            {fieldState.error && (
              <FieldError match={true}>{fieldState.error.message}</FieldError>
            )}
          </FieldRoot>
        )}
      />

      <div className="flex gap-4">
        <Controller
          control={control}
          name="city"
          render={({ field, fieldState }) => (
            <FieldRoot
              className="flex-1"
              invalid={!!fieldState.error}
              name={field.name}
            >
              <FieldLabel>City</FieldLabel>
              <FieldControl
                render={(defaultProps) => (
                  <Input
                    {...defaultProps}
                    {...field}
                    autoComplete="off"
                    className={defaultProps.className ?? ""}
                    placeholder="City"
                    style={defaultProps.style ?? {}}
                  />
                )}
              />
              {fieldState.error && (
                <FieldError match={true}>{fieldState.error.message}</FieldError>
              )}
            </FieldRoot>
          )}
          rules={{
            required: "City is required",
            minLength: { value: 2, message: "City name is too short" },
            pattern: {
              value: /^[A-Za-z\s.\-']+$/,
              message: "City can only contain letters, spaces, and hyphens",
            },
          }}
        />

        <Controller
          control={control}
          name="state"
          render={({ field, fieldState }) => (
            <FieldRoot
              className="flex-1"
              invalid={!!fieldState.error}
              name={field.name}
            >
              <FieldLabel>State</FieldLabel>
              <Combobox
                autoHighlight={true}
                items={US_STATES}
                onValueChange={(val) => {
                  field.onChange(val?.toUpperCase() ?? "");
                }}
                value={field.value || null}
              >
                <ComboboxInput onBlur={field.onBlur} placeholder="e.g. NY" />
                <ComboboxPopup>
                  <ComboboxList>
                    {(item: string) => (
                      <ComboboxItem key={item} value={item}>
                        {item}
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                  <ComboboxEmpty>No matching state</ComboboxEmpty>
                </ComboboxPopup>
              </Combobox>
              {fieldState.error && (
                <FieldError match={true}>{fieldState.error.message}</FieldError>
              )}
            </FieldRoot>
          )}
          rules={{
            required: "State is required",
            validate: (v) =>
              US_STATES.includes(
                v.toUpperCase() as (typeof US_STATES)[number],
              ) || "Enter a valid US state",
          }}
        />
      </div>

      <Controller
        control={control}
        name="zip"
        render={({ field, fieldState }) => (
          <FieldRoot invalid={!!fieldState.error} name={field.name}>
            <FieldLabel>ZIP Code</FieldLabel>
            <FieldControl
              render={(defaultProps) => (
                <Input
                  {...defaultProps}
                  {...field}
                  autoComplete="off"
                  className={defaultProps.className ?? ""}
                  inputMode="numeric"
                  maxLength={5}
                  onChange={(e) =>
                    field.onChange(e.target.value.replace(/\D/g, ""))
                  }
                  placeholder="e.g. 10001"
                  style={defaultProps.style ?? {}}
                />
              )}
            />
            {fieldState.error && (
              <FieldError match={true}>{fieldState.error.message}</FieldError>
            )}
          </FieldRoot>
        )}
        rules={{
          required: "ZIP code is required",
          pattern: {
            value: zipCodeRegex,
            message: "ZIP code must be 5 digits",
          },
        }}
      />
    </Fieldset>
  );
}
